import { Dialog } from "construct-ui";
import IncentiveSchemeVariableModel from "../../models/incentive-scheme-variable/incentive-scheme-variable.model";
import ViewBase from "../view-base.view";

/**
 * Incentive Scheme Clone Dialog
 *
 * Copies a scheme and all of its configuration - components, parameters, rate
 * matrix, AR ladders, caps, grades and disqualification rules - to a new version
 * and period.
 *
 * This is the dominant real workflow: each quarter starts from the last one, and
 * cloning is how that happens without editing a closed scheme. The clone lands as
 * DRAFT, so it can be adjusted before it pays anyone.
 *
 * @author FFIS
 */
export default function IncentiveSchemeCloneDialog() {
  ViewBase.call(this);

  const FORM_ID = 'IncentiveSchemeCloneForm';

  const DEFAULT_PAYLOAD = {
    newSchemeVersion: '',
    newPeriodFrom: '',
    newPeriodTo: '',
    newSchemeName: '',
  };

  let close = () => { };
  let schemeId = 0;
  let payload = structuredClone(DEFAULT_PAYLOAD);

  const handleSubmit = (e) => {
    if (!this.validateForm(e, `#${FORM_ID}`)) {
      return false;
    }

    this.showLoading();
    IncentiveSchemeVariableModel.cloneScheme(schemeId, payload)
      .then((result) => {
        const trans = Array.isArray(result) ? result[0] : result?.transResult?.[0];
        this.showMessage(
          this.getText(trans?.code, trans?.message),
          trans?.transStatus,
          () => { close(true); });
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  };

  const dateField = (labelCode, fallback, prop, required) =>
    m('.col-md-6.position-relative.mb-4.g-2',
      m(`label.form-label${required ? '.mandatory' : ''}[for=${prop}]`,
        this.getText(labelCode, fallback)),
      m(`input.form-control.form-control-bg[pattern=\\d{8}]${required ? '[required]' : ''}`, {
        id: prop,
        placeholder: 'YYYYMMDD',
        maxlength: 8,
        value: payload[prop] ?? '',
        onchange: (e) => { payload[prop] = e.target.value.trim(); },
      }),
      m('.invalid-tooltip', this.getText('LT00000082', 'Enter a date as YYYYMMDD')));

  return {
    oninit: (vnode) => {
      if (vnode.attrs.closeDialog) {
        close = vnode.attrs.closeDialog;
      }
      schemeId = vnode.attrs.schemeId || 0;
      payload = structuredClone(DEFAULT_PAYLOAD);
    },

    view: (vnode) => {
      return m(Dialog, {
        class: 'cui-dialog-lg',
        isOpen: vnode.attrs.isOpen,
        closeOnOutsideClick: false,
        onClose: () => { vnode.attrs.closeDialog(false); },
        title: this.getText('LT00000081', 'Clone'),
        content: [
          m('container',
            m('.card',
              m('.card-body',
                m('form.needs-validation.row.g-2[novalidate]',
                  { id: FORM_ID, onsubmit: handleSubmit },

                  m('.col-md-6.position-relative.mb-4.g-2',
                    m('label.form-label.mandatory[for=newSchemeVersion]',
                      this.getText('LT00000003', 'New Version')),
                    m('input.form-control.form-control-bg[required][maxlength=20]', {
                      id: 'newSchemeVersion',
                      value: payload.newSchemeVersion,
                      onchange: (e) => {
                        payload.newSchemeVersion = e.target.value.trim();
                      },
                    }),
                    m('.invalid-tooltip',
                      this.getText('LT00000729', 'This field is required'))),

                  m('.col-md-6.position-relative.mb-4.g-2',
                    m('label.form-label[for=newSchemeName]',
                      this.getText('LT00000002', 'New Name')),
                    m('input.form-control.form-control-bg[maxlength=200]', {
                      id: 'newSchemeName',
                      placeholder: this.getText('LT00000086', 'Leave blank to keep the current name'),
                      value: payload.newSchemeName,
                      onchange: (e) => { payload.newSchemeName = e.target.value.trim(); },
                    })),

                  dateField('LT00000010', 'New Effective From', 'newPeriodFrom', true),
                  dateField('LT00000011', 'New Effective To', 'newPeriodTo', false),

                  m('.col-12',
                    m('.alert.alert-info.py-2',
                      m('small', this.getText('EM00000274',
                        'Every component, parameter, rate, ladder, cap, grade and disqualification rule is copied. The clone is created as DRAFT.')))),

                  m('.col-md-12.gap-2.d-flex.justify-content-end',
                    m("button.btn.btn-primary[type='submit']",
                      this.getText('LT00000022', 'Save')),
                    m("button.btn.btn-white[type='button']",
                      { onclick: () => { close(false); } },
                      this.getText('LT00000021', 'Close')))))))
          ,
          this.getBaseViews(),
        ],
      });
    },
  };
}
